// frontend/src/pages/teacher/TeacherDashboard.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../../modules/api';
import {
    FaPlusCircle, FaTasks, FaUserGraduate, FaChartBar,
    FaBookOpen, FaClipboardCheck, FaUsers, FaSpinner
} from 'react-icons/fa';
import '../../assets/styles/global.css';

// ==============================================================================
// Helper Component: Summary Widget
// ==============================================================================
const SummaryWidget = ({ icon, value, label, color, loading }) => (
    <div className={`summary-widget color-${color}`}>
        <div className="widget-icon-container">{icon}</div>
        <div className="widget-info">
            {loading ? (
                <FaSpinner className="spinner" />
            ) : (
                <>
                    <p className="widget-value">{value}</p>
                    <p className="widget-label">{label}</p>
                </>
            )}
        </div>
    </div>
);

// ==============================================================================
// Data Structures for Action Cards
// ==============================================================================
const actionCards = [
    { icon: <FaPlusCircle />, title: "ایجاد تکلیف جدید", description: "طراحی یک تکلیف استاندارد، آزمون یا پروژه برای کلاس.", action: "/teacher/assignments/create", color: "primary" },
    { icon: <FaTasks />, title: "مدیریت تکالیف", description: "مشاهده، ویرایش و بررسی تکالیف ارسال شده.", action: "/teacher/assignments", color: "info" },
    { icon: <FaUserGraduate />, title: "کلاس‌ها و دانش‌آموزان", description: "مدیریت لیست دانش‌آموزان و مشاهده پروفایل‌ها.", action: "/teacher/students", color: "success" },
    { icon: <FaChartBar />, title: "گزارش‌ها و تحلیل‌ها", description: "دسترسی به گزارش‌های پیشرفت تحصیلی و عملکرد.", action: "/teacher/analytics", color: "warning" }
];

// ==============================================================================
// Main Component: TeacherDashboard
// ==============================================================================
const TeacherDashboard = () => {
    const navigate = useNavigate();
    const [summaryData, setSummaryData] = useState({
        course_count: 0,
        active_assignment_count: 0,
        student_count: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSummaryData = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/core/teacher/dashboard-summary/`, getAuthHeaders());
                setSummaryData(response.data);
            } catch (error) {
                console.error("Error fetching dashboard summary:", error);
                // در صورت خطا، می‌توان پیام مناسبی به کاربر نمایش داد
            } finally {
                setLoading(false);
            }
        };
        fetchSummaryData();
    }, []);

    const widgets = [
        { icon: <FaClipboardCheck />, value: summaryData.active_assignment_count, label: "تکلیف فعال", color: "blue" },
        { icon: <FaUsers />, value: summaryData.student_count, label: "دانش‌آموز", color: "green" },
        { icon: <FaBookOpen />, value: summaryData.course_count, label: "دوره در حال تدریس", color: "orange" },
    ];

    return (
        <div className="dashboard-container">
            {/* Summary Widgets Section */}
            <section className="summary-widgets-grid">
                {widgets.map((widget, index) => (
                    <SummaryWidget key={index} {...widget} loading={loading} />
                ))}
            </section>

            {/* Action Cards Section */}
            <section className="action-cards-grid">
                {actionCards.map((card, index) => (
                    <div key={index} className={`action-card color-border-${card.color}`} onClick={() => navigate(card.action)}>
                        <div className={`action-card-icon color-bg-${card.color}`}>{card.icon}</div>
                        <h3 className="action-card-title">{card.title}</h3>
                        <p className="action-card-description">{card.description}</p>
                        <span className={`action-card-link color-text-${card.color}`}>ورود به بخش &larr;</span>
                    </div>
                ))}
            </section>
        </div>
    );
};

export default TeacherDashboard;